/**
 * Entwurf der Schnellerfassung (C6): liegt nur im Browser (`localStorage`), je Konto und Anmeldung, und verfällt nach
 * 7 Tagen. Nach dem Speichern und beim Abmelden wird er gelöscht, damit die nächste Person am Gerät ihn nicht sieht.
 * Jeder Zugriff ist abgesichert: ohne Speicher (privates Fenster, gesperrt) arbeitet die Seite einfach ohne Entwurf.
 */
export const BULK_DRAFT_PREFIX = 'barso.bulk.';
export const BULK_DRAFT_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

export interface BulkDraftRow {
  code: string;
  title: string;
  price: string;
  category: string;
  size: string;
}

export interface BulkDraft {
  savedAt: number;
  rows: BulkDraftRow[];
}

const keyOf = (userId: string, registrationId: string): string =>
  `${BULK_DRAFT_PREFIX}${userId}.${registrationId}`;

const isRow = (value: unknown): value is BulkDraftRow => {
  if (typeof value !== 'object' || value === null) return false;
  const row = value as Record<string, unknown>;
  return ['code', 'title', 'price', 'category', 'size'].every(
    (key) => typeof row[key] === 'string',
  );
};

/** Liest den Entwurf; abgelaufene oder unlesbare Entwürfe werden gelöscht und ergeben `null`. */
export function loadBulkDraft(
  userId: string,
  registrationId: string,
  nowMs: number = Date.now(),
): BulkDraft | null {
  const key = keyOf(userId, registrationId);
  try {
    const text = window.localStorage.getItem(key);
    if (text === null) return null;
    const parsed: unknown = JSON.parse(text);
    if (typeof parsed === 'object' && parsed !== null) {
      const draft = parsed as { savedAt?: unknown; rows?: unknown };
      if (
        typeof draft.savedAt === 'number' &&
        Array.isArray(draft.rows) &&
        nowMs - draft.savedAt < BULK_DRAFT_MAX_AGE_MS &&
        draft.rows.every(isRow) &&
        draft.rows.length > 0
      ) {
        return { savedAt: draft.savedAt, rows: draft.rows };
      }
    }
    window.localStorage.removeItem(key);
  } catch {
    // Speicher nicht verfügbar oder Inhalt kaputt: ohne Entwurf weiterarbeiten
  }
  return null;
}

/** Beim Öffnen: abgelaufene oder unlesbare Entwürfe dieses Kontos (alle Anmeldungen) entfernen, damit nichts liegen bleibt. */
export function purgeExpiredBulkDrafts(userId: string, nowMs: number = Date.now()): void {
  try {
    const prefix = `${BULK_DRAFT_PREFIX}${userId}.`;
    const stale: string[] = [];
    for (let index = 0; index < window.localStorage.length; index += 1) {
      const key = window.localStorage.key(index);
      if (key?.startsWith(prefix) !== true) continue;
      let keep = false;
      try {
        const parsed: unknown = JSON.parse(window.localStorage.getItem(key) ?? '');
        const savedAt =
          typeof parsed === 'object' && parsed !== null
            ? (parsed as { savedAt?: unknown }).savedAt
            : undefined;
        keep = typeof savedAt === 'number' && nowMs - savedAt < BULK_DRAFT_MAX_AGE_MS;
      } catch {
        keep = false;
      }
      if (!keep) stale.push(key);
    }
    for (const key of stale) window.localStorage.removeItem(key);
  } catch {
    // Speicher nicht verfügbar
  }
}

export function saveBulkDraft(
  userId: string,
  registrationId: string,
  rows: BulkDraftRow[],
  nowMs: number = Date.now(),
): void {
  try {
    if (rows.length === 0) {
      window.localStorage.removeItem(keyOf(userId, registrationId));
      return;
    }
    const draft: BulkDraft = { savedAt: nowMs, rows };
    window.localStorage.setItem(keyOf(userId, registrationId), JSON.stringify(draft));
  } catch {
    // Speicher voll oder gesperrt: der Entwurf geht dann nur beim Verlassen der Seite verloren
  }
}

export function clearBulkDraft(userId: string, registrationId: string): void {
  try {
    window.localStorage.removeItem(keyOf(userId, registrationId));
  } catch {
    // nichts zu löschen
  }
}

/** Beim Abmelden und bei beendeter Sitzung: alle Entwürfe dieses Geräts entfernen (egal für welches Konto). */
export function clearAllBulkDrafts(): void {
  try {
    const keys: string[] = [];
    for (let index = 0; index < window.localStorage.length; index += 1) {
      const key = window.localStorage.key(index);
      if (key?.startsWith(BULK_DRAFT_PREFIX) === true) keys.push(key);
    }
    for (const key of keys) window.localStorage.removeItem(key);
  } catch {
    // Speicher nicht verfügbar
  }
}
